import { faLink } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useState } from 'react'
import z from 'zod'
import { useOrderApplicantStatusMutation } from '~/api/mutations/order'
import { useTaxonomy } from '~/api/queries/taxonomy'
import { Dropdown, Form, InputCol, Submit } from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/Submit'
import { ButtonVariation } from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/Toast'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { OrdersAutocomplete } from '~/domains/orders/components/OrdersAutocomplete'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'
import { toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z.object({
  orderId: z.coerce
    .number({ errorMap: () => ({ message: 'Commande requise' }) })
    .min(1, 'Commande requise'),
  statusId: z.coerce
    .number({ errorMap: () => ({ message: 'Statut requis' }) })
    .min(1, 'Statut requis')
})

type FormValues = z.infer<typeof formSchema>

type AddToOrderActionProps = {
  applicantIds: number[]
}

export const AddToOrderAction = ({ applicantIds }: AddToOrderActionProps) => {
  const { setIsDirty } = useContext(AsideActionContext)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const closeAction = useCloseAction()

  const { data: applicantOrderStatuses, isLoading: isLoadingStatuses } =
    useTaxonomy('applicant_order_statuses')

  const addApplicantToOrderStatusMutation = useOrderApplicantStatusMutation(
    selectedOrderId ?? undefined
  )

  const onSubmit = async (values: FormValues) =>
    addApplicantToOrderStatusMutation.mutateAsync(
      {
        applicants: applicantIds,
        status: values.statusId
      },
      {
        onSuccess: () => {
          toast(
            applicantIds.length > 1 ? 'Candidats associés' : 'Candidat associé',
            applicantIds.length > 1
              ? 'Candidats associés avec succès.'
              : 'Candidat associé avec succès.',
            ToastType.Success
          )
          closeAction(true)
        },
        onError: () => {
          toast(
            'Erreur',
            "Impossible d'associer les candidats à la commande. Veuillez réessayer.",
            ToastType.Danger
          )
        }
      }
    )

  return (
    <AsideAction
      title={'Associer à une commande'}
      icon={faLink}
      applicantIds={applicantIds}
    >
      <Form onSubmit={onSubmit} schema={formSchema}>
        <InputCol>
          <UsersAutocomplete
            label="Filtrer par utilisateur"
            placeholder="Choisir un utilisateur"
            onChange={(value) => {
              setSelectedUserId(value ? Number.parseInt(value) : null)
              setIsDirty(true)
            }}
          />

          <OrdersAutocomplete
            label="Commandes ouvertes"
            name="orderId"
            required
            placeholder="Choisir une commande"
            userId={selectedUserId ?? undefined}
            onChange={(value) => {
              setSelectedOrderId(value ? Number.parseInt(value) : null)
              setIsDirty(true)
            }}
          />

          <Dropdown
            label="Statut"
            name="statusId"
            placeholder="Choisir un statut"
            required
            options={
              applicantOrderStatuses
                ?.filter((status) => status.specialStatus !== 'accepted')
                .map((status) => ({
                  label: status.name,
                  value: status.id.toString()
                })) ?? []
            }
            defaultValue={applicantOrderStatuses
              ?.find((status) => status.specialStatus === 'selected')
              ?.id.toString()}
            optionsLoading={isLoadingStatuses}
            emptyMessage="Aucun statut disponible"
            onChange={() => setIsDirty(true)}
          />

          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Association en cours..."
            successText="Associé !"
            onSuccessReset={false}
          >
            <FontAwesomeIcon icon={faLink} />
            Associer à la commande
          </Submit>
        </InputCol>
      </Form>
    </AsideAction>
  )
}
